<script>
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Overlay from 'ol/Overlay'
import { LineString, Polygon } from 'ol/geom'
import { Draw, Snap } from 'ol/interaction'
import { getArea, getLength } from 'ol/sphere'
import { unByKey } from 'ol/Observable'
import { WKT } from 'ol/format'
import parseStyle from '../ol-map/utils/style'

// 画笔样式
const defaultBrush = {
  fill: 'rgba(255, 223, 64, 0.5)',
  stroke: {
    color: 'rgb(255, 223, 64)', // 黄色
    width: 2
  },
  circle: {
    radius: 4,
    fill: 'red',
    stroke: {
      width: 2,
      color: '#fff'
    }
  }
}

// 图形样式
const defaultEffect = {
  fill: 'rgba(209, 60, 116, 0.5)',
  stroke: {
    color: 'rgb(209, 60, 116)', // 菠根红
    width: 2
  }
}

// 地图实例
let olMap = null

/**
 * 当前绘制的图形
 * @type {import("../src/ol/Feature.js").default}
 */
let sketch

/**
 * 测量提示元素
 * @type {HTMLElement}
 */
const measureTooltipElement = document.createElement('div')
measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure'

/**
 * Overlay 实例
 * @type {Overlay}
 */
const measureTooltip = new Overlay({
  element: measureTooltipElement,
  offset: [0, -15],
  positioning: 'bottom-center',
  stopEvent: false,
  insertFirst: false
})

// 监听函数
let listener

export default {
  name: 'ol-draw',

  inject: ['getMap'],

  render () {
    return this.$slots.default
  },

  props: {
    // 画笔样式
    brush: {
      type: [Object, Function],
      default () {
        return defaultBrush
      }
    },

    // 图形样式
    effect: {
      type: [Object, Function],
      default () {
        return defaultEffect
      }
    },

    // 停用或激活
    active: {
      type: Boolean,
      default: false
    },

    // 绘制类型
    type: {
      type: String,
      default: 'Polygon'
    },

    // 是否测量
    measure: {
      type: Boolean,
      default: true
    }
  },

  watch: {
    active (newVal) {
      if (!newVal) {
        this.vectorLayer.getSource().clear()
        measureTooltip.setPosition(undefined)
      }
      this.drawInstance.setActive(newVal)
    },

    type () {
      this.vectorLayer.getSource().clear()
      measureTooltip.setPosition(undefined)
      olMap.removeInteraction(this.drawInstance)
      this.draw(olMap)
    },

    measure () {
      this.vectorLayer.getSource().clear()
      measureTooltip.setPosition(undefined)
    }
  },

  data () {
    return {
      vectorLayer: null,
      // 绘画实例
      drawInstance: null,
      // 绘画开始处理函数
      drawStartHandler: null,
      // 绘画结束处理函数
      drawEndHandler: null,
      // 捕捉交互修改地图浏览器事件坐标和像素属性
      snapInstance: null
    }
  },

  created () {
    // https://newgateway.gitee.io/my/api/map.html?url=%2Fmy%2Fapi%2Fmap%2Fmodule-%24ui_map_my-map-draw.html
    // https://newgateway.gitee.io/my/api/map/packages_my-map-draw_Draw.js.html
    const source = new VectorSource()
    this.vectorLayer = new VectorLayer({
      name: 'draw',
      source,
      style: parseStyle(this.effect)
    })

    this.getMap(map => {
      olMap = map
      // const layerLength = map.getLayers().getArray().length
      map.addLayer(this.vectorLayer)
      this.vectorLayer.setZIndex(99)
      this.draw(map)
    })
  },

  methods: {
    draw (map) {
      const source = this.vectorLayer.getSource()
      this.snapInstance = new Snap({ source })
      this.drawInstance = new Draw({
        type: this.type,
        source,
        style: parseStyle(this.brush)
      })

      this.drawInstance.setActive(this.active)

      this.drawStartHandler = evt => {
        source.clear()
        measureTooltip.setPosition(undefined)
        sketch = evt.feature

        let tooltipCoord = evt.coordinate

        listener = sketch.getGeometry().on('change', evt => {
          const geom = evt.target
          let output
          if (geom instanceof Polygon) {
            output = this.formatArea(geom)
            tooltipCoord = geom.getInteriorPoint().getCoordinates()
          } else if (geom instanceof LineString) {
            output = this.formatLength(geom)
            tooltipCoord = geom.getLastCoordinate()
          }
          if (this.measure) {
            measureTooltipElement.innerHTML = output
            measureTooltip.setPosition(tooltipCoord)
          }
        })
      }

      this.drawEndHandler = evt => {
        measureTooltipElement.className = 'ol-tooltip ol-tooltip-static'
        sketch = null
        map.addOverlay(measureTooltip)
        unByKey(listener)
        if (!this.measure) {
          this.$emit('drawend', new WKT().writeGeometry(evt.feature.getGeometry()))
        }
      }

      this.drawInstance.on('drawstart', this.drawStartHandler)
      this.drawInstance.on('drawend', this.drawEndHandler)

      map.addInteraction(this.snapInstance)
      map.addInteraction(this.drawInstance)
    },

    formatLength (line) {
      const length = getLength(line, { projection: 'EPSG:4326' })
      let output
      if (length > 100) {
        output = (Math.round(length / 1000 * 100) / 100) + ' km'
      } else {
        output = (Math.round(length * 100) / 100) + ' m'
      }
      return output
    },

    formatArea (polygon) {
      const area = getArea(polygon, { projection: 'EPSG:4326' })
      let output
      if (area > 10000) {
        output = (Math.round(area / 1000000 * 100) / 100) + ' km<sup>2</sup>'
      } else {
        output = (Math.round(area * 100) / 100) + ' m<sup>2</sup>'
      }
      return output
    }
  },

  beforeDestroy () {
    this.drawInstance.un('drawstart', this.drawStartHandler)
    this.drawInstance.un('drawend', this.drawEndHandler)
    olMap.removeLayer(this.vectorLayer)
  }
}
</script>

<style lang="scss">
.ol-tooltip {
  position: relative;
  font-size: 12px;
  color: white;
  white-space: nowrap;
  padding: 4px 8px;
  border-radius: 4px;
  user-select: none;
  opacity: 0.7;
}

.ol-tooltip-measure {
  opacity: 1;
  font-weight: bold;
}

.ol-tooltip-static {
  color: #fff;
  border: 1px solid #fff;
  background-color: rgba(209, 60, 116, .8);
}

.ol-tooltip-measure::before, .ol-tooltip-static::before {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -6px;
  transform: translateX(-50%);
  border-top: 6px solid #fff;
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
}

.ol-tooltip-static::before {
  border-top-color: rgba(209, 60, 116, .8);
}
</style>
